import React, { useMemo, useRef, useState } from "react";
import { client } from "../../..";
import { WA_CREATE_TEMPLATE, WA_EDIT_TEMPLATE } from "../../../Queries/WhatsApp";
import SidePanel from "./SidePanel";
import { VARIABLE_SOURCE_LABELS, gqlErrorMessage } from "./waUtils";

const PLACEHOLDER_RX = /\{\{\s*(\d+)\s*\}\}/g;
const BUTTON_KINDS = {
  QUICK_REPLY: { label: "Quick reply", icon: "fa-reply" },
  URL: { label: "Visit website", icon: "fa-arrow-up-right-from-square" },
  PHONE_NUMBER: { label: "Call phone number", icon: "fa-phone" },
};
const CATEGORY_HELP = {
  UTILITY: "Updates about something the contact already has with us — documents received, status updates, reminders. Cheaper, and no marketing restrictions.",
  MARKETING: "Promotions, offers, outreach and follow-ups to prospects. Needs a recorded opt-in. Meta may move a utility template here if it reads like marketing.",
};

/** Distinct numbered variables in order of first appearance: ["1","2",…]. */
const varsIn = (text) => {
  const out = [];
  for (const m of String(text || "").matchAll(PLACEHOLDER_RX)) if (!out.includes(m[1])) out.push(m[1]);
  return out;
};

const slugName = (v) => v.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");

const fromTemplate = (t, mode) => {
  const headerVars = varsIn(t.headerText).length;
  return {
    name: mode === "duplicate" ? `${t.name}_copy` : t.name,
    language: t.language,
    category: t.category === "MARKETING" ? "MARKETING" : "UTILITY",
    headerText: t.headerText || "",
    bodyText: t.bodyText || "",
    footerText: t.footerText || "",
    buttons: (t.buttons || [])
      .filter((b) => BUTTON_KINDS[b.type])
      .map((b) => ({ type: b.type, text: b.text || "", url: b.url || "", phone: b.phone || "" })),
    examples: { header: t.examples?.header || "", body: [...(t.examples?.body || [])] },
    // Stored map is header var first, then body vars.
    headerVar: (t.variables || []).slice(0, headerVars)[0] || null,
    bodyVars: (t.variables || []).slice(headerVars).map((v) => ({ source: v.source, label: v.label })),
  };
};

const EMPTY = {
  name: "",
  language: "en_US",
  category: "UTILITY",
  headerText: "",
  bodyText: "",
  footerText: "",
  buttons: [],
  examples: { header: "", body: [] },
  headerVar: null,
  bodyVars: [],
};

const Counter = ({ value, max }) => (
  <span style={{ float: "right", fontWeight: 400, color: value.length > max ? "var(--wa-danger)" : "var(--wa-faint)" }}>{value.length}/{max}</span>
);

/**
 * Create / edit / duplicate a message template. Submits to Meta for review.
 * mode: "create" | "edit" | "duplicate"; meta: { limits, languages, categories }.
 */
const TemplateEditorPanel = ({ mode = "create", template = null, meta, onClose, onSaved }) => {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(() => (template ? fromTemplate(template, mode) : EMPTY));
  const [error, setError] = useState(null); // { field, message }
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);
  const limits = meta.limits;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const headerKeys = useMemo(() => varsIn(form.headerText), [form.headerText]);
  const bodyKeys = useMemo(() => varsIn(form.bodyText), [form.bodyText]);

  const insertBodyVariable = () => {
    const token = `{{${bodyKeys.length + 1}}}`;
    const el = bodyRef.current;
    const start = el ? el.selectionStart : form.bodyText.length;
    const end = el ? el.selectionEnd : form.bodyText.length;
    const next = form.bodyText.slice(0, start) + token + form.bodyText.slice(end);
    set({ bodyText: next });
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const setBodyExample = (i, v) => setForm((f) => {
    const body = [...f.examples.body];
    body[i] = v;
    return { ...f, examples: { ...f.examples, body } };
  });
  const setBodyVar = (i, patch) => setForm((f) => {
    const bodyVars = [...f.bodyVars];
    bodyVars[i] = { source: "custom", label: "", ...bodyVars[i], ...patch };
    return { ...f, bodyVars };
  });
  const setButton = (i, patch) => setForm((f) => ({ ...f, buttons: f.buttons.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));
  const addButton = (type) => setForm((f) => ({ ...f, buttons: [...f.buttons, { type, text: "", url: "", phone: "" }] }));
  const removeButton = (i) => setForm((f) => ({ ...f, buttons: f.buttons.filter((_, j) => j !== i) }));

  const countOf = (type) => form.buttons.filter((b) => b.type === type).length;
  const canAdd = {
    QUICK_REPLY: form.buttons.length < limits.maxButtons,
    URL: form.buttons.length < limits.maxButtons && countOf("URL") < limits.maxUrlButtons,
    PHONE_NUMBER: form.buttons.length < limits.maxButtons && countOf("PHONE_NUMBER") < limits.maxPhoneButtons,
  };

  // Sample-filled preview, exactly as the contact will see it.
  const fill = (text, keys, values) => String(text || "").replace(PLACEHOLDER_RX, (whole, k) => {
    const v = values[keys.indexOf(k)];
    return v ? v : whole;
  });
  const previewHeader = fill(form.headerText, headerKeys, [form.examples.header]);
  const previewBody = fill(form.bodyText, bodyKeys, form.examples.body);

  const submit = async () => {
    if (saving) return;
    setError(null);
    if (!isEdit && !form.name) return setError({ field: "name", message: "Give the template a name." });
    if (!form.bodyText.trim()) return setError({ field: "bodyText", message: "The message body can't be empty." });
    setSaving(true);
    const draft = {
      name: form.name,
      language: form.language,
      category: form.category,
      headerText: form.headerText,
      bodyText: form.bodyText,
      footerText: form.footerText,
      buttons: form.buttons,
      examples: { header: form.examples.header, body: bodyKeys.map((_, i) => form.examples.body[i] || "") },
      variables: [
        ...(headerKeys.length ? [form.headerVar || { source: "custom", label: "" }] : []),
        ...bodyKeys.map((_, i) => form.bodyVars[i] || { source: i === 0 ? "contact_first_name" : "custom", label: "" }),
      ],
    };
    try {
      const { data } = isEdit
        ? await client.mutate({ mutation: WA_EDIT_TEMPLATE, variables: { id: template.id, draft } })
        : await client.mutate({ mutation: WA_CREATE_TEMPLATE, variables: { draft } });
      const res = isEdit ? data.whatsappEditTemplate : data.whatsappCreateTemplate;
      if (!res?.ok) {
        setError({ field: res?.field || null, message: res?.error || "Meta did not accept the template." });
        return;
      }
      onSaved?.(res.template, mode);
    } catch (err) {
      setError({ field: null, message: gqlErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  const bad = (field) => error?.field === field || (field === "buttons" && error?.field?.startsWith("buttons"));
  const fieldStyle = (field) => (bad(field) ? { borderColor: "var(--wa-danger)" } : undefined);
  const sourceOptions = Object.entries(VARIABLE_SOURCE_LABELS);

  return (
    <SidePanel
      wide
      title={isEdit ? `Edit ${template.name}` : mode === "duplicate" ? "Duplicate template" : "New template"}
      subtitle={isEdit
        ? "Saving sends the change to Meta for review. Approved templates can be edited once a day, up to 10 times a month."
        : "New templates are reviewed by Meta — usually within minutes, sometimes up to 24 hours. Only approved templates can be sent."}
      onClose={onClose}
      footer={
        <>
          {error ? (
            <span className="wa-help" style={{ color: "var(--wa-danger)", margin: 0, flex: 1 }} role="alert">{error.message}</span>
          ) : (
            <span className="wa-help" style={{ margin: 0, flex: 1 }}><span className="wa-kbd">Ctrl</span>+<span className="wa-kbd">Enter</span> to submit</span>
          )}
          <button type="button" className="wa-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="wa-btn wa-btn--primary" disabled={saving} onClick={submit}>
            {saving ? <i className="fa-solid fa-spinner wa-spin" /> : <i className="fa-solid fa-paper-plane" />} {isEdit ? "Save & resubmit" : "Submit for review"}
          </button>
        </>
      }
    >
      <div
        className="wa-tpl-editor"
        onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit(); } }}
      >
        <div className="wa-tpl-editor__form">
          {isEdit ? (
            <div className="wa-field">
              <div className="wa-label">Template</div>
              <div>
                <b>{template.name}</b> <span className="wa-chip">{template.language}</span>{" "}
                <span className={`wa-chip ${template.category === "MARKETING" ? "wa-chip--warn" : "wa-chip--accent"}`}>{String(template.category).toLowerCase()}</span>
              </div>
              <div className="wa-help" style={{ marginTop: 4 }}>Name, language and category can't change once submitted — duplicate the template instead.</div>
            </div>
          ) : (
            <>
              <div className="wa-field">
                <label className="wa-label" htmlFor="wa-tpl-name">Name <Counter value={form.name} max={limits.nameMax} /></label>
                <input id="wa-tpl-name" className="wa-input" style={fieldStyle("name")} value={form.name} placeholder="e.g. renewal_reminder"
                  onChange={(e) => set({ name: slugName(e.target.value) })} />
                <div className="wa-help" style={{ marginTop: 4 }}>Lowercase letters, numbers and underscores. The team sees this name in the template picker.</div>
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div className="wa-field" style={{ flex: "1 1 160px" }}>
                  <label className="wa-label" htmlFor="wa-tpl-cat">Category</label>
                  <select id="wa-tpl-cat" className="wa-select" style={fieldStyle("category")} value={form.category} onChange={(e) => set({ category: e.target.value })}>
                    {meta.categories.map((c) => <option key={c} value={c}>{c.charAt(0) + c.slice(1).toLowerCase()}</option>)}
                  </select>
                </div>
                <div className="wa-field" style={{ flex: "1 1 160px" }}>
                  <label className="wa-label" htmlFor="wa-tpl-lang">Language</label>
                  <select id="wa-tpl-lang" className="wa-select" style={fieldStyle("language")} value={form.language} onChange={(e) => set({ language: e.target.value })}>
                    {meta.languages.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="wa-help" style={{ marginTop: -6, marginBottom: 14 }}>{CATEGORY_HELP[form.category]}</div>
            </>
          )}

          <div className="wa-field">
            <label className="wa-label" htmlFor="wa-tpl-header">Header <span style={{ fontWeight: 400, color: "var(--wa-faint)" }}>(optional)</span> <Counter value={form.headerText} max={limits.headerMax} /></label>
            <div style={{ display: "flex", gap: 6 }}>
              <input id="wa-tpl-header" className="wa-input" style={fieldStyle("headerText")} value={form.headerText} placeholder="Bold first line, e.g. Renewal reminder"
                onChange={(e) => set({ headerText: e.target.value.replace(/[\r\n]/g, "") })} />
              {!headerKeys.length && (
                <button type="button" className="wa-btn wa-btn--sm" style={{ height: 34 }} onClick={() => set({ headerText: `${form.headerText}${form.headerText ? " " : ""}{{1}}` })} title="Add a variable to the header">
                  + Variable
                </button>
              )}
            </div>
          </div>

          <div className="wa-field">
            <label className="wa-label" htmlFor="wa-tpl-body">Message <Counter value={form.bodyText} max={limits.bodyMax} /></label>
            <textarea id="wa-tpl-body" ref={bodyRef} className="wa-textarea" rows={6} style={fieldStyle("bodyText")} value={form.bodyText}
              placeholder={"Hi {{1}}, your renewal for {{2}} is due next week. Reply here if you have any questions."}
              onChange={(e) => set({ bodyText: e.target.value })} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, gap: 8, flexWrap: "wrap" }}>
              <span className="wa-help" style={{ margin: 0 }}>*bold* _italic_ ~strike~ · variables can't be first or last, or next to each other.</span>
              <button type="button" className="wa-btn wa-btn--sm" onClick={insertBodyVariable}>+ Add variable {`{{${bodyKeys.length + 1}}}`}</button>
            </div>
          </div>

          {(headerKeys.length > 0 || bodyKeys.length > 0) && (
            <div className="wa-field">
              <div className="wa-label">Variables</div>
              <div className="wa-help" style={{ marginBottom: 8 }}>
                <b>Sample</b> values are shown to Meta's reviewers — use realistic ones. <b>Auto-fill</b> is what the team's composer pre-fills.
              </div>
              <table className="wa-table">
                <thead><tr><th style={{ width: 70 }}>Var</th><th>Sample (for review)</th><th>Auto-fill</th><th>Label for the team</th></tr></thead>
                <tbody>
                  {headerKeys.map((k) => (
                    <tr key={`h${k}`}>
                      <td><code>{`{{${k}}}`}</code><div className="wa-help" style={{ margin: 0 }}>header</div></td>
                      <td><input className="wa-input" style={fieldStyle("examples.header")} value={form.examples.header} maxLength={limits.exampleMax}
                        onChange={(e) => set({ examples: { ...form.examples, header: e.target.value } })} placeholder="e.g. March payroll" /></td>
                      <td>
                        <select className="wa-select" value={form.headerVar?.source || "custom"} onChange={(e) => set({ headerVar: { ...(form.headerVar || { label: "" }), source: e.target.value } })}>
                          {sourceOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </td>
                      <td><input className="wa-input" value={form.headerVar?.label || ""} maxLength={120} placeholder="Header value"
                        onChange={(e) => set({ headerVar: { source: "custom", ...(form.headerVar || {}), label: e.target.value } })} /></td>
                    </tr>
                  ))}
                  {bodyKeys.map((k, i) => (
                    <tr key={`b${k}`}>
                      <td><code>{`{{${k}}}`}</code></td>
                      <td><input className="wa-input" style={fieldStyle(`examples.body.${i}`)} value={form.examples.body[i] || ""} maxLength={limits.exampleMax}
                        onChange={(e) => setBodyExample(i, e.target.value)} placeholder={i === 0 ? "e.g. Joan" : "e.g. JMR Insurance"} /></td>
                      <td>
                        <select className="wa-select" value={form.bodyVars[i]?.source || (i === 0 ? "contact_first_name" : "custom")} onChange={(e) => setBodyVar(i, { source: e.target.value })}>
                          {sourceOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </td>
                      <td><input className="wa-input" value={form.bodyVars[i]?.label || ""} maxLength={120} placeholder={`Variable ${k}`}
                        onChange={(e) => setBodyVar(i, { label: e.target.value })} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="wa-field">
            <label className="wa-label" htmlFor="wa-tpl-footer">Footer <span style={{ fontWeight: 400, color: "var(--wa-faint)" }}>(optional)</span> <Counter value={form.footerText} max={limits.footerMax} /></label>
            <input id="wa-tpl-footer" className="wa-input" style={fieldStyle("footerText")} value={form.footerText} placeholder="e.g. Reply STOP to opt out"
              onChange={(e) => set({ footerText: e.target.value })} />
          </div>

          <div className="wa-field">
            <div className="wa-label">Buttons <span style={{ fontWeight: 400, color: "var(--wa-faint)" }}>(optional, up to {limits.maxButtons})</span></div>
            {form.buttons.map((b, i) => (
              <div key={i} className="wa-tpl-btnrow" style={bad(`buttons.${i}`) ? { borderColor: "var(--wa-danger)" } : undefined}>
                <span className="wa-chip"><i className={`fa-solid ${BUTTON_KINDS[b.type].icon}`} /> {BUTTON_KINDS[b.type].label}</span>
                <input className="wa-input" value={b.text} maxLength={limits.buttonTextMax} placeholder="Button text" onChange={(e) => setButton(i, { text: e.target.value })} />
                {b.type === "URL" && <input className="wa-input" value={b.url} placeholder="https://…" onChange={(e) => setButton(i, { url: e.target.value })} />}
                {b.type === "PHONE_NUMBER" && <input className="wa-input" value={b.phone} placeholder="+15105550123" onChange={(e) => setButton(i, { phone: e.target.value })} />}
                <button type="button" className="wa-icon-btn" aria-label="Remove button" title="Remove" onClick={() => removeButton(i)}><i className="fa-solid fa-trash-can" /></button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {Object.entries(BUTTON_KINDS).map(([type, k]) => (
                <button key={type} type="button" className="wa-btn wa-btn--sm" disabled={!canAdd[type]} onClick={() => addButton(type)}>
                  <i className={`fa-solid ${k.icon}`} /> {k.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="wa-tpl-editor__preview">
          <div className="wa-label">Preview</div>
          <div className="wa-preview">
            <div className="wa-msg wa-msg--out">
              <div className="wa-bubble">
                {previewHeader && <div style={{ fontWeight: 700, marginBottom: 4 }}>{previewHeader}</div>}
                <div className="wa-bubble__text">{previewBody || <span style={{ color: "var(--wa-faint)" }}>Your message appears here.</span>}</div>
                {form.footerText && <div style={{ color: "var(--wa-muted)", fontSize: 12, marginTop: 6 }}>{form.footerText}</div>}
                {form.buttons.length > 0 && (
                  <div style={{ borderTop: "1px solid var(--wa-line)", marginTop: 8, paddingTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                    {form.buttons.map((b, i) => (
                      <span key={i} style={{ color: "var(--wa-accent)", fontWeight: 600, textAlign: "center", fontSize: 12.5 }}>
                        <i className={`fa-solid ${BUTTON_KINDS[b.type].icon}`} style={{ fontSize: 10, marginRight: 4 }} />{b.text || BUTTON_KINDS[b.type].label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="wa-help" style={{ marginTop: 8 }}>Sample values fill the variables here. Real messages use the team's auto-fill or typed values.</div>
        </div>
      </div>
    </SidePanel>
  );
};

export default TemplateEditorPanel;
